import { addMinutes, isAfter } from 'date-fns';
import Request from './Request';

export interface IOverview {
	id: number;
	name: string;
	code: string;
	featured: boolean;
	level: number;
	year: number;
	lastYear: IOverview;
	children: IOverview[];
	planSRE: number;
	planFRE: number;
	planBusinessPlan: number;
	actual: number;
	approver: string;
	isApproved: boolean;
}

export interface IChannelTS {
	name: string;
	planSRE: number;
	planFRE: number;
	planBusinessPlan: number;
}
export interface IChannelFC {
	name: string;
	actual: number;
}

export interface IOverviewChannelType {
	id: number;
	name: string;
	slug: string;
}

export interface IOverviewChannel {
	id: number;
	name: string;
	code: string;
	channelTypeSlug: string;
	featured: boolean;
	level: number;
	year: number;
	latitude: number;
	longitude: number;
	order: number;
	lastYear: IOverviewChannel;
	children: IOverviewChannel[];
	planChannels: IChannelTS[];
	actualChannels: IChannelFC[];
	parentId: number;
}

export function getOverviewTS(obj: IOverview, tstype1: string): number {

	if (obj == null)
		return 0;

	const valueBase: number =
		tstype1 === "BP" ? obj.planBusinessPlan :
			tstype1 === "SRE" ? obj.planSRE :
						tstype1 === "FRE" ? obj.planFRE :
							0;
	return valueBase;
}

export function getChannelTS(obj: IChannelTS, tstype1: string): number {

	if (obj == null)
		return 0;

	const valueBase: number =
		tstype1 === "BP" ? obj.planBusinessPlan :
			tstype1 === "SRE" ? obj.planSRE :
						tstype1 === "FRE" ? obj.planFRE :
							0;
	return valueBase;
}
interface IOverviewCacheEntry { promise: Promise<IOverview>; expiry: Date; }

const _overviewCache    = new Map<string, IOverviewCacheEntry>();
const _ytdOverviewCache = new Map<string, IOverviewCacheEntry>();

export function clearOverviewCache() {
	_overviewCache.clear();
	_ytdOverviewCache.clear();
}

function _getOverviewCached(cache: Map<string, IOverviewCacheEntry>, key: string): Promise<IOverview> | null {
	const entry = cache.get(key);
	if (!entry) return null;
	if (isAfter(new Date(), entry.expiry)) { cache.delete(key); return null; }
	return entry.promise;
}

function _setOverviewCached(cache: Map<string, IOverviewCacheEntry>, key: string, promise: Promise<IOverview>) {
	cache.set(key, { promise, expiry: addMinutes(new Date(), 5) });
	// Failed requests (auth errors, network errors, etc.) must not be served from cache.
	promise.catch(() => cache.delete(key));
}

class Dashboard {
	static GetOverview = (code: string, year: number, month: number, channelType: string): Promise<IOverview> => {
		const key = `${code}|${year}|${month}|${channelType}`;
		const cached = _getOverviewCached(_overviewCache, key);
		if (cached) return cached;
		const promise = Request.DoGet(`dashboard/overview/${code}/${year}/${month}?channelType=${channelType}`) as unknown as Promise<IOverview>;
		_setOverviewCached(_overviewCache, key, promise);
		return promise;
	};

	static GetYTDOverview = (code: string, year: number, month: number, channelType: string): Promise<IOverview> => {
		const key = `${code}|${year}|${month}|${channelType}`;
		const cached = _getOverviewCached(_ytdOverviewCache, key);
		if (cached) return cached;
		const promise = Request.DoGet(`dashboard/overview/ytd/${code}/${year}/${month}?channelType=${channelType}`) as unknown as Promise<IOverview>;
		_setOverviewCached(_ytdOverviewCache, key, promise);
		return promise;
	};

	static GetChannel = (code: string, year: number, month: number, channelType: string): IOverviewChannel => {
		return Request.DoGet(`dashboard/channel/${code}/${year}/${month}?channelType=${channelType}`) as unknown as IOverviewChannel;
	};

	static GetYTDChannel = (code: string, year: number, month: number, channelType: string): IOverviewChannel => {
		return Request.DoGet(`dashboard/channel/ytd/${code}/${year}/${month}?channelType=${channelType}`) as unknown as IOverviewChannel;
	};

}

export default Dashboard;